import React, { useState, useEffect } from "react";
import { FaCoins, FaTimes, FaSave } from "react-icons/fa";
import apiRequest from "../services/api";

const RegisterWithholds = ({ isOpen, invoice, onClose, onSave }) => {
  const [form, setForm] = useState({
    registered: false,
    invoiceId: "",
    whReceipt: "",
    vwhReceipt: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      const registered = Boolean(invoice?.whRegistered);

      setForm({
        invoiceId: invoice?.id || "",
        registered,
        whReceipt: invoice?.whReceiptNumber || "",
        vwhReceipt: invoice?.vatWhReceiptNumber || "",
      });
      setIsEditing(!registered);
      setError("");
    }
  }, [isOpen, invoice]);

  if (!isOpen) return null;

  const canEdit = !form.registered;
  const mode = form.registered ? "View" : "Register";

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    if (!form.invoiceId) {
      setError("Invoice ID is missing.");
      return;
    }

    if (!form.whReceipt.trim() && !form.vwhReceipt.trim()) {
      setError("Enter at least one withholding receipt number.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const params = new URLSearchParams({
        whReciept: form.whReceipt.trim(),
        vWhReciept: form.vwhReceipt.trim(),
      });

      const response = await apiRequest(
        `invoices/${form.invoiceId}/register-withholdings?${params}`,
        { method: "PUT" }
      );

      await onSave?.(response?.data || response);
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to register withholdings.");
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-emerald-600 dark:border-white/10 dark:bg-slate-800 dark:text-white";

  const fields = [
    ["whReceipt", "Withholding Receipt Number"],
    ["vwhReceipt", "VAT Withholding Receipt Number"],
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-0 backdrop-blur-sm sm:p-4">
      <div className="flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900 sm:h-auto sm:max-h-[90vh] sm:max-w-xl sm:rounded-2xl">
        {/* HEADER */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-white/10">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-emerald-100 p-3 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
              <FaCoins size={22} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                {mode} Withholdings
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                Invoice #{form.invoiceId || "—"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-xl p-2.5 text-slate-500 hover:bg-slate-100 hover:text-rose-600 disabled:opacity-50 dark:hover:bg-white/10"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 space-y-5 overflow-y-auto p-5 sm:p-7">
          {fields.map(([name, label]) => (
            <div key={name}>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">
                {label}
              </label>

              {isEditing && canEdit ? (
                <input
                  name={name}
                  value={form[name]}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder={`Enter ${label.toLowerCase()}`}
                />
              ) : (
                <p className="break-all rounded-lg bg-slate-50 px-3 py-3 text-sm font-medium text-slate-800 dark:bg-slate-800 dark:text-white">
                  {form[name] || "—"}
                </p>
              )}
            </div>
          ))}

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-400">
              {error}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex shrink-0 justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-white/10 dark:bg-slate-900">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10"
          >
            Close
          </button>

          {canEdit && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave className="mr-2 inline" />
              {saving ? "Registering..." : "Register"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterWithholds;
